import { Platform } from 'react-native';
import { useWatch } from 'react-hook-form';
import { Column, Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { useFormContext } from 'app/hooks/form';
import { useNavigation } from 'app/hooks/navigation';
import { useIsExpired } from 'app/hooks/use-is-expired';
import { SERVICE_CATEGORY_ID, useCanUseServices, useSimplifiedServices } from 'app/network/category';
import { useUser } from 'app/network/user';
import { ListingSubtype, ListingType, type CreateListingDTO } from 'app/types/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { useTranslation } from 'react-i18next';
import { useCreationTab } from '../creation-tab-context';

export function ListingTypeToggle({ disabled = false }: { disabled?: boolean }) {
  const { t } = useTranslation('offer-hub');
  const pressableType = Platform.OS === 'web' ? PressableType.Default : PressableType.GestureHandler;
  const { t: tNew } = useTranslation('new');
  const { control, setValue, clearErrors } = useFormContext<CreateListingDTO>();
  const type = useWatch({ control, name: 'type' });
  const subtype = useWatch({ control, name: 'subtype' });
  const { data: user } = useUser();
  const canUseServices = useCanUseServices();
  const simplifiedServices = useSimplifiedServices();
  const authorizeRequest = useIsExpired();
  const navigation = useNavigation();
  const creationTab = useCreationTab();
  const canDonate =
    Boolean(user?.canPublishDonations) && (type === ListingType.request || !isWhiteLabel(WhiteLabel.HauteGaspesie));

  const changeType = (next: ListingType) => {
    if (next === type) return;
    const change = () => {
      setValue('type', next, { shouldDirty: true });
      // Service requests cannot become offers. Keep the shared title, text, and visibility draft.
      if (
        subtype === ListingSubtype.service ||
        (next === ListingType.offer &&
          subtype === ListingSubtype.donation &&
          (!user?.canPublishDonations || isWhiteLabel(WhiteLabel.HauteGaspesie)))
      ) {
        setValue('subtype', ListingSubtype.object, { shouldDirty: true });
        setValue('serviceCategoryId', null);
        setValue('categoryPrimaryId', null);
      }
      setValue('duplicates', null);
      setValue('automaticMessage', '');
      clearErrors();
    };
    if (next === ListingType.request) authorizeRequest(change);
    else change();
  };

  const changeSubtype = (next: ListingSubtype) => {
    if (next === subtype) return;
    setValue('subtype', next, { shouldDirty: true });
    setValue('serviceCategoryId', next === ListingSubtype.service ? simplifiedServices || null : null);
    setValue('categoryPrimaryId', next === ListingSubtype.service ? SERVICE_CATEGORY_ID : null);
    clearErrors();
    if (next === ListingSubtype.service && !simplifiedServices) {
      navigation.navigate('serviceCategories', { goBackTo: creationTab ? 'publish' : 'new' });
    }
  };

  const subtypes = [
    { value: ListingSubtype.object, label: t('object') },
    ...(canDonate ? [{ value: ListingSubtype.donation, label: tNew(`prompt.${type}.donation`) }] : []),
    ...(type === ListingType.request && canUseServices
      ? [{ value: ListingSubtype.service, label: tNew('prompt.request.service') }]
      : []),
  ];

  return (
    <Column gap="spacing.general.x3" marginBottom="spacing.general.x4">
      <Row
        alignSelf="center"
        width={248}
        maxWidth="100%"
        backgroundColor="surface.primary-neutral"
        borderRadius="radius.lg"
        padding="spacing.general.x1"
        gap="spacing.general.x1"
        opacity={disabled ? 0.5 : 1}
        accessibilityRole="tablist"
      >
        {[ListingType.offer, ListingType.request].map((value) => (
          <Button
            key={value}
            flex={1}
            minHeight={36}
            hitSlop={{ top: 4, bottom: 4 }}
            disabled={disabled}
            accessibilityRole="tab"
            accessibilityState={{ selected: type === value }}
            testID={`creation-type-${value}`}
            onPress={() => changeType(value)}
            pressableType={pressableType}
            variant="plain"
          >
            <Row
              width="100%"
              minHeight={36}
              alignItems="center"
              justifyContent="center"
              paddingHorizontal="spacing.general.x4"
              paddingVertical="spacing.general.x2"
              borderRadius="radius.md"
              backgroundColor={type === value ? 'surface.default' : undefined}
              shadowColor="text.primary"
              shadowOffset={{ width: 0, height: 1 }}
              shadowOpacity={type === value ? 0.1 : 0}
              shadowRadius={3}
              elevation={type === value ? 2 : 0}
            >
              <Text
                color={type === value ? 'text.primary' : 'text.secondary'}
                fontSize={14}
                lineHeight={20}
                fontWeight="600"
                textAlign="center"
              >
                {t(`listingTypes.${value}`)}
              </Text>
            </Row>
          </Button>
        ))}
      </Row>
      {subtypes.length > 1 ? (
        <Row flexWrap="wrap" gap="spacing.general.x2">
          {subtypes.map(({ value, label }) => (
            <Button
              key={value}
              disabled={disabled}
              onPress={() => changeSubtype(value)}
              accessibilityState={{ selected: subtype === value }}
              testID={`creation-subtype-${value}`}
              pressableType={pressableType}
              variant={subtype === value ? 'primary' : 'secondary'}
            >
              <Text color={subtype === value ? 'surface.default' : 'text.primary'}>{label}</Text>
            </Button>
          ))}
        </Row>
      ) : null}
    </Column>
  );
}
